import React, { FC, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import { trackGAEvent, useTrackGAEventOnce } from '@moonpig/web-core-analytics'
import { createLinkHref } from '@moonpig/web-core-link'
import { Carousel } from '@moonpig/web-shared-components'
import { MISSION_BUTTONS_CAROUSEL_THRESHOLD } from '@moonpig/web-core-brand/config'
import { ModuleMissionButtonsFragment } from './__generated__/fragment'
import { ModuleSection } from '../../components/ModuleContainer'
import { useTracking } from './tracking'
import { Button } from '../../components/MissionButton/Button'
import { Module, ModuleContext } from '../types'
import { ImageButton } from '../../components/MissionButton/ImageButton'

import { RemindersButton } from '../../components/MissionButton/RemindersButton'
import { filterSupportedOccasions } from '../../components/MissionButton/RemindersButton/utils'
import { getModulePadding } from './getModulePadding'

type MissionButtonsWithRemindersProps = {
  module: ModuleMissionButtonsFragment
  context: ModuleContext
  ref: React.Ref<HTMLDivElement>
  inView: boolean
}

// this is largely duplicated from MissionButtonsModule, but with reminders to keep the integrity of the original module
const MissionButtonsWithReminders: FC<MissionButtonsWithRemindersProps> = ({
  module: { missionButtons, meta, ...rest },
  context,
  ref: refProp,
  inView,
}) => {
  const validReminders =
    context.remindersResult && 'reminders' in context.remindersResult
      ? context.remindersResult.reminders
      : []
  const supportedReminders = filterSupportedOccasions(validReminders)
  const shouldShowReminders = supportedReminders.length > 0
  const shouldShowImageMissionButtons = context.layout === 'home'
  const reminderOccasion = supportedReminders[0]?.occasion
  const { viewList, selectItem, selectReminderItem } = useTracking(
    missionButtons,
    meta,
    {
      hasRemindersButton: !!reminderOccasion,
      reminderOccasion: reminderOccasion ?? undefined,
    },
  )

  const mapMissionButtonTitles = () => {
    return missionButtons.map(item => item.title).join(', ')
  }

  const reminderItem =
    shouldShowReminders && reminderOccasion ? (
      <RemindersButton
        key="reminders"
        onTrackClickEvent={selectReminderItem(reminderOccasion)}
        reminders={validReminders}
      />
    ) : null

  const missionButtonItems = missionButtons.map((item, index) => {
    if (shouldShowImageMissionButtons) {
      return item.image ? (
        <ImageButton
          key={item.title}
          title={item.title}
          linkHref={createLinkHref({
            url: item.url,
            trackEvent: selectItem(index),
          })}
          imageUrl={item.image}
        />
      ) : null
    }

    return (
      <Button
        key={item.title}
        title={item.title}
        linkHref={createLinkHref({
          url: item.url,
          trackEvent: selectItem(index),
        })}
      />
    )
  })

  const carouselItems = [reminderItem, ...missionButtonItems].filter(Boolean)
  useEffect(() => {
    if (inView) {
      trackGAEvent(viewList())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView])

  return (
    <ModuleSection
      {...rest}
      {...context}
      data-testid="module-mission-buttons"
      withContainer
      fullWidthOnMobile
      ref={refProp}
      padding={getModulePadding(shouldShowImageMissionButtons)}
    >
      <Carousel
        ariaLabel={`${mapMissionButtonTitles()}`}
        hideScrollButtons={
          carouselItems.length < MISSION_BUTTONS_CAROUSEL_THRESHOLD
        }
      >
        {carouselItems}
      </Carousel>
    </ModuleSection>
  )
}

export const MissionButtonsModule: FC<Module<ModuleMissionButtonsFragment>> = ({
  module: { missionButtons, meta, ...rest },
  context,
}) => {
  const hasValidReminders =
    context.remindersResult !== undefined &&
    'reminders' in context.remindersResult &&
    context.remindersResult.reminders.length > 0

  const [ref, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  })

  const isHomePage = context.layout === 'home'
  const shouldShowImageMissionButtons =
    isHomePage ||
    (context.layout === 'landing' &&
      missionButtons.every(button => button.image))

  const { viewList, selectItem } = useTracking(missionButtons, meta)
  const { trackGAEventOnce } = useTrackGAEventOnce()

  const remindersTimedOut =
    context.remindersResult !== undefined &&
    'timedOut' in context.remindersResult

  useEffect(() => {
    if (remindersTimedOut) {
      trackGAEventOnce({
        event: 'impression',
        event_data: {
          category: 'home',
          action: 'reminder mission button | timeout',
          label: 'mission buttons',
          value: undefined,
          non_interaction: true,
        },
      })
    }
  }, [remindersTimedOut, trackGAEventOnce])

  const missionButtonItems = missionButtons.map((item, index) => {
    if (isHomePage) {
      return item.image ? (
        <ImageButton
          key={item.title}
          title={item.title}
          linkHref={createLinkHref({
            url: item.url,
            trackEvent: selectItem(index),
          })}
          imageUrl={item.image}
        />
      ) : null
    }
    return shouldShowImageMissionButtons && item.image ? (
      <ImageButton
        key={item.title}
        title={item.title}
        linkHref={createLinkHref({
          url: item.url,
          trackEvent: selectItem(index),
        })}
        imageUrl={item.image}
      />
    ) : (
      <Button
        key={item.title}
        title={item.title}
        linkHref={createLinkHref({
          url: item.url,
          trackEvent: selectItem(index),
        })}
      />
    )
  })

  const carouselItems = missionButtonItems.filter(Boolean)

  const mapMissionButtonTitles = () => {
    return missionButtons.map(item => item.title).join(', ')
  }

  useEffect(() => {
    if (inView) {
      trackGAEvent(viewList())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView])

  if (hasValidReminders) {
    return (
      <MissionButtonsWithReminders
        module={{ missionButtons, meta, ...rest }}
        context={context}
        ref={ref}
        inView={inView}
      />
    )
  }

  return (
    <ModuleSection
      {...rest}
      {...context}
      data-testid="module-mission-buttons"
      withContainer
      fullWidthOnMobile
      ref={ref}
      padding={getModulePadding(shouldShowImageMissionButtons)}
    >
      <Carousel
        ariaLabel={`${mapMissionButtonTitles()}`}
        hideScrollButtons={
          missionButtons.length < MISSION_BUTTONS_CAROUSEL_THRESHOLD
        }
      >
        {carouselItems}
      </Carousel>
    </ModuleSection>
  )
}
